import { lazy, Suspense, useContext, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { Circle, MapContainer, Marker, Polyline, Popup, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { tripLine, type LatLng, type MapWaypoint } from '../lib/geo';
import { isBar } from '../lib/places';
import type { PlaceSummary } from '../lib/types';
import PlacePopup from './PlacePopup';
import { MapEngineContext } from './MapEngine';
import { pinFor, pinSize, pinSvg } from './pinIcons';

// Inside the mobile shell every map is MapLibre; loaded on demand so the website doesn't ship it.
const GlPlaceMap = lazy(() => import('./GlPlaceMap'));

export type Bounds = [south: number, west: number, north: number, east: number];

interface Props {
  /** Beer mugs for bars (a mug and a burger when they serve food), food glyphs for bites. */
  places?: PlaceSummary[];
  /** Numbered stops, in order. */
  stops?: PlaceSummary[];
  /** Draw a line through the stops (closing the loop when isLoop). */
  connectStops?: boolean;
  isLoop?: boolean;
  /** Where the trip starts and finishes: the stop line runs from one through the stops to the other. */
  routeFrom?: LatLng | null;
  routeTo?: LatLng | null;
  /** Route lines as [lat, lng] arrays. */
  lines?: [number, number][][];
  /** Lettered A, B, C... unless they carry their own tag (S and F for a trip's start and finish). */
  waypoints?: MapWaypoint[];
  circle?: { lat: number; lng: number; radiusMiles: number };
  /** Points to fit the view to; refits whenever fitKey changes. */
  fitPoints?: [number, number][];
  fitKey?: string;
  center?: [number, number];
  zoom?: number;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  onBoundsChange?: (bounds: Bounds, zoom: number) => void;
  className?: string;
  label?: string;
}

const MILE = 1609.344;

// CARTO's basemaps need a key (free tier at carto.com/basemaps/apikey); without one every tile
// carries an "API key required" watermark. It is baked in at build time and is sent with every
// tile request, so it is not a secret.
const CARTO_KEY: string | undefined = import.meta.env.VITE_CARTO_KEY;
const TILE_URL =
  'https://basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}{r}.png' +
  (CARTO_KEY ? `?key=${encodeURIComponent(CARTO_KEY)}` : '');

export default function PlaceMap(props: Props) {
  const engine = useContext(MapEngineContext);
  if (engine === 'gl') {
    return (
      <Suspense fallback={<div className={`rounded-2xl border border-line bg-[#1a1d1b] ${props.className ?? 'h-[480px]'}`} />}>
        <GlPlaceMap {...props} />
      </Suspense>
    );
  }
  return <LeafletPlaceMap {...props} />;
}

export type { Props as PlaceMapProps };

function LeafletPlaceMap({
  places = [],
  stops = [],
  connectStops,
  isLoop,
  routeFrom,
  routeTo,
  lines = [],
  waypoints = [],
  circle,
  fitPoints,
  fitKey,
  center = [44.3, -86.0],
  zoom = 9,
  selectedId,
  onSelect,
  onBoundsChange,
  className = 'h-[480px]',
  label = 'Map of bars and bites',
}: Props) {
  const stopIds = useMemo(() => new Set(stops.map((s) => s.id)), [stops]);
  const [mapZoom, setMapZoom] = useState(zoom);
  const stopLine = useMemo<[number, number][]>(
    () => (connectStops ? tripLine(stops, routeFrom, routeTo, isLoop).map((p) => [p.lat, p.lng] as [number, number]) : []),
    [connectStops, isLoop, stops, routeFrom?.lat, routeFrom?.lng, routeTo?.lat, routeTo?.lng],
  );

  return (
    <div className={`overflow-hidden rounded-2xl border border-line ${className}`} role="region" aria-label={label}>
      <MapContainer center={center} zoom={zoom} preferCanvas className="h-full w-full" scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url={TILE_URL}
          maxZoom={19}
        />

        {circle && (
          <Circle
            center={[circle.lat, circle.lng]}
            radius={circle.radiusMiles * MILE}
            pathOptions={{ color: '#6CC5B0', weight: 2, dashArray: '6 6', fillOpacity: 0.05 }}
          />
        )}

        {lines.map((line, i) => (
          <Polyline key={`line-${i}`} positions={line} pathOptions={{ color: '#F2A93B', weight: 4, opacity: 0.9 }} />
        ))}

        {stopLine.length > 1 && (
          <>
            <Polyline positions={stopLine} pathOptions={{ color: '#F2A93B', weight: 12, opacity: 0.15 }} />
            <Polyline positions={stopLine} pathOptions={{ color: '#F2A93B', weight: 4, dashArray: '10 7' }} />
          </>
        )}

        {places
          .filter((p) => !stopIds.has(p.id))
          .map((p) => {
            const selected = p.id === selectedId;
            return (
              <Marker
                key={p.id}
                position={[p.lat, p.lng]}
                icon={placeIcon(p, selected, mapZoom < COMPACT_BELOW_ZOOM)}
                zIndexOffset={selected ? 1000 : (p.isCurated ? 200 : 0) + p.backroadScore}
                eventHandlers={{ click: () => onSelect?.(p.id) }}
              >
                <Popup>
                  <PlacePopup place={p} />
                </Popup>
              </Marker>
            );
          })}

        {stops.map((s, i) => (
          <Marker key={s.id} position={[s.lat, s.lng]} icon={numberIcon(i + 1, !isBar(s.kind))} eventHandlers={{ click: () => onSelect?.(s.id) }}>
            <Popup>
              <PlacePopup place={s} number={i + 1} />
            </Popup>
          </Marker>
        ))}

        {waypoints.map((w, i) => (
          <Marker key={`wp-${i}`} position={[w.lat, w.lng]} icon={waypointIcon(w.tag ?? String.fromCharCode(65 + i))}>
            <Popup>{w.label}</Popup>
          </Marker>
        ))}

        <FitBounds points={fitPoints} fitKey={fitKey} />
        {onBoundsChange && <BoundsWatcher onChange={onBoundsChange} />}
        <ZoomWatcher onChange={setMapZoom} />
      </MapContainer>
    </div>
  );
}

function FitBounds({ points, fitKey }: { points?: [number, number][]; fitKey?: string }) {
  const map = useMap();
  const last = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!points?.length || last.current === fitKey) return;
    last.current = fitKey;
    if (points.length === 1) {
      map.setView(points[0], 12);
    } else {
      map.fitBounds(L.latLngBounds(points), { padding: [36, 36], maxZoom: 13 });
    }
  }, [map, points, fitKey]);

  return null;
}

function BoundsWatcher({ onChange }: { onChange: (b: Bounds, zoom: number) => void }) {
  const report = (m: L.Map) => {
    const b = m.getBounds();
    onChange([b.getSouth(), b.getWest(), b.getNorth(), b.getEast()], m.getZoom());
  };
  const map = useMapEvents({ moveend: (e) => report(e.target as L.Map) });
  useEffect(() => {
    report(map);
  }, [map]);
  return null;
}

const iconCache = new Map<string, L.DivIcon>();

/** Beer mug, mug and burger, or a food glyph, ringed in the place's score color (pinIcons.ts). */
function placeIcon(p: PlaceSummary, selected: boolean, compact: boolean): L.DivIcon {
  const pin = pinFor(p, selected);
  const key = `${pin.image}:${compact ? 'c' : ''}`;
  if (!iconCache.has(key)) {
    const { w, h } = pinSize(pin.glyph, selected, compact);
    iconCache.set(
      key,
      L.divIcon({ className: '', html: pinSvg(pin.glyph, pin.ring, selected, compact), iconSize: [w, h], iconAnchor: [w / 2, h / 2], popupAnchor: [0, -h / 2] }),
    );
  }
  return iconCache.get(key)!;
}

/** Below city zoom the pins shrink so a region's worth doesn't pile up. */
const COMPACT_BELOW_ZOOM = 10;

function ZoomWatcher({ onChange }: { onChange: (zoom: number) => void }) {
  useMapEvents({ zoomend: (e) => onChange((e.target as L.Map).getZoom()) });
  return null;
}

function numberIcon(n: number, bite: boolean): L.DivIcon {
  const key = `n${n}${bite ? 'b' : ''}`;
  if (!iconCache.has(key)) {
    iconCache.set(
      key,
      L.divIcon({ className: '', html: `<div class="stop-pin${bite ? ' stop-pin-bite' : ''}" style="width:28px;height:28px">${n}</div>`, iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -14] }),
    );
  }
  return iconCache.get(key)!;
}

function waypointIcon(letter: string): L.DivIcon {
  const key = `w${letter}`;
  if (!iconCache.has(key)) {
    iconCache.set(
      key,
      L.divIcon({ className: '', html: `<div class="waypoint-pin" style="width:26px;height:26px">${letter}</div>`, iconSize: [26, 26], iconAnchor: [13, 13], popupAnchor: [0, -13] }),
    );
  }
  return iconCache.get(key)!;
}
